import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  getFirestore,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const PAGE_SIZE = 100;

const DailyProduction = () => {
  const [barcodes, setBarcodes] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  // Fetch first page on load
  useEffect(() => {
    fetchInitial();
  }, []);

  const fetchInitial = async () => {
    setLoading(true);
    const q = query(collection(db, "factorybarcode"), orderBy("BarcodeNumber"), limit(PAGE_SIZE));
    const snap = await getDocs(q);
    setLastDoc(snap.docs[snap.docs.length - 1]);

    const results = await enrichBarcodes(snap.docs);
    setBarcodes(results);
    setLoading(false);
  };

  const loadMore = async () => {
    if (!lastDoc) return;

    setLoading(true);
    const q = query(
      collection(db, "factorybarcode"),
      orderBy("BarcodeNumber"),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );
    const snap = await getDocs(q);
    const newResults = await enrichBarcodes(snap.docs);
    setLastDoc(snap.docs[snap.docs.length - 1] || null);
    setBarcodes(prev => [...prev, ...newResults]);
    setLoading(false);
  };

  const enrichBarcodes = async (docs) => {
    const enriched = [];

    for (const docSnap of docs) {
      const data = docSnap.data();
      const instancesSnap = await getDocs(collection(db, "factorybarcode", docSnap.id, "instances"));
      const instances = instancesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      enriched.push({ id: docSnap.id, ...data, instances });
    }

    return enriched;
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }

    setLoading(true);
    const snap = await getDocs(collection(db, "factorybarcode"));
    const allResults = await enrichBarcodes(snap.docs);

    const filtered = allResults.filter((item) => {
      const term = searchTerm.toLowerCase();
      return (
        item.SerialNumber?.toLowerCase().includes(term) ||
        item.ProductCode?.toLowerCase().includes(term) ||
        item.Program?.toLowerCase().includes(term)
      );
    });

    setSearchResults(filtered);
    setLoading(false);
  };

  const displayedData = searchResults !== null ? searchResults : barcodes;

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-4">Daily Production Report</h2>

      {/* Search */}
      <div className="flex items-center space-x-2 mb-4">
        <input
          type="text"
          placeholder="Search Serial, Product Code, or Program"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border rounded w-80 shadow-sm"
        />
        <button
          onClick={handleSearch}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-hover"
        >
          Search
        </button>
        {searchResults !== null && (
          <button
            onClick={() => {
              setSearchTerm("");
              setSearchResults(null);
            }}
            className="ml-2 text-sm text-red-500 underline"
          >
            Clear search
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded bg-white shadow">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Barcode</th>
              <th className="px-4 py-2 text-left">Serial</th>
              <th className="px-4 py-2 text-left">Product Code</th>
              <th className="px-4 py-2 text-left">Program</th>
              <th className="px-4 py-2 text-left">Qty</th>
              <th className="px-4 py-2 text-left">Instances</th>
            </tr>
          </thead>
          <tbody>
            {displayedData.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-500">
                  No data found.
                </td>
              </tr>
            )}
            {displayedData.map((item) => (
              <React.Fragment key={item.id}>
                <tr className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2">{item.BarcodeNumber}</td>
                  <td className="px-4 py-2">{item.SerialNumber}</td>
                  <td className="px-4 py-2">{item.ProductCode}</td>
                  <td className="px-4 py-2">{item.Program}</td>
                  <td className="px-4 py-2">{item.Quantity}</td>
                  <td className="px-4 py-2">
                    {item.instances.length > 0 ? (
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className="text-primary hover:underline flex items-center space-x-1"
                      >
                        {expanded[item.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        <span>{item.instances.length}</span>
                      </button>
                    ) : (
                      <span className="text-gray-400 italic">None</span>
                    )}
                  </td>
                </tr>

                {expanded[item.id] && (
  <tr className="bg-gray-50">
    <td colSpan={6} className="p-4">
      <table className="w-full table-fixed text-xs border border-gray-300">
        <colgroup>
          <col style={{ width: "25%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "20%" }} />
        </colgroup>
        <thead className="bg-gray-200 text-gray-700">
          <tr>
            <th className="px-2 py-1 text-left">Printed At</th>
            <th className="px-2 py-1 text-left">Stretch</th>
            <th className="px-2 py-1 text-left">Stretch Time</th>
            <th className="px-2 py-1 text-left">Despatch</th>
            <th className="px-2 py-1 text-left">Despatch Time</th>
            <th className="px-2 py-1 text-left">Hortonwood</th>
            <th className="px-2 py-1 text-left">Hortonwood Time</th>
          </tr>
        </thead>
        <tbody>
          {item.instances.map((inst) => (
            <tr key={inst.id} className="border-t border-gray-200">
              <td className="px-2 py-1">{inst.PrintedAt?.toDate().toLocaleString() || "-"}</td>
              <td className="px-2 py-1">{inst.stretch_scan ? "✅" : "❌"}</td>
              <td className="px-2 py-1">{inst.stretch_scantime?.toDate?.()?.toLocaleString?.() || "-"}</td>
              <td className="px-2 py-1">{inst.despatch_scan ? "✅" : "❌"}</td>
              <td className="px-2 py-1">{inst.despatch_scantime?.toDate?.()?.toLocaleString?.() || "-"}</td>
              <td className="px-2 py-1">{inst.hortonwood_scan ? "✅" : "❌"}</td>
              <td className="px-2 py-1">{inst.hortonwood_scantime?.toDate?.()?.toLocaleString?.() || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </td>
  </tr>
)}

              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load More */}
      {searchResults === null && lastDoc && (
        <div className="mt-4 text-center">
          <button
            onClick={loadMore}
            className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
          >
            Load More
          </button>
        </div>
      )}

      {loading && (
        <div className="text-center mt-4 text-gray-600 flex justify-center items-center space-x-2">
          <Loader2 className="animate-spin" size={20} />
          <span>Loading...</span>
        </div>
      )}
    </div>
  );
};

export default DailyProduction;
